import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { BookOpen, Sparkles, Search, Filter, ArrowUp } from 'lucide-react'
import { BIBLE_BOOKS } from '../config/bibleConfig'
import { BibleBook } from '../types/bible'
import BookCard from '../components/BookCard'
import ParticleBackground from '../components/ParticleBackground'
import Footer from '../components/Footer'

const HomePage: React.FC = () => {
  const navigate = useNavigate()
  const [books, setBooks] = useState<BibleBook[]>(BIBLE_BOOKS)
  const [filter, setFilter] = useState<'all' | 'old' | 'new'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [showScrollTop, setShowScrollTop] = useState(false)

  // Handle scroll to show/hide scroll to top button
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY
      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.scrollHeight
      
      // Show button when scrolled down more than 300px or near bottom (within 200px)
      const isNearBottom = scrollPosition + windowHeight >= documentHeight - 200
      const isScrolledDown = scrollPosition > 300
      
      setShowScrollTop(isScrolledDown || isNearBottom)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }

  useEffect(() => {
    let filtered = BIBLE_BOOKS

    // Filter by testament
    if (filter !== 'all') {
      filtered = filtered.filter(book => book.testament === filter)
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        book =>
          book.name.toLowerCase().includes(query) ||
          book.abbreviation.toLowerCase().includes(query) ||
          book.description?.toLowerCase().includes(query)
      )
    }

    setBooks(filtered)
  }, [filter, searchQuery])

  const handleSelectBook = (book: BibleBook) => {
    // Navigate to book reader page
    navigate(`/book/${book.id}`)
  }

  const handleGenerateSlides = (book: BibleBook, chapter?: number) => {
    // Navigate to Slideshow generator with pre-filled reference  
    const reference = chapter ? `${book.name} ${chapter}` : `${book.name} 1`
    navigate(`/slideshow-generator?reference=${encodeURIComponent(reference)}`)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative">
      <ParticleBackground />
      
      <div className="relative z-10 container mx-auto px-4 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-5xl md:text-6xl font-bold text-bible-gold mb-4">
            Bible Reader
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Read the bible in a modern, engaging way.
          </p>

          {/* Quick Actions */}
          <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
            <a
              href="/slideshow-generator"
              className="px-6 py-3 bg-bible-gold text-slate-900 rounded-lg font-semibold hover:bg-yellow-500 transition-all flex items-center gap-2 shadow-lg"
            >
              <Sparkles size={20} />
              Slideshow Generator
            </a>
          </div>
        </motion.div>

        {/* Search and Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="max-w-4xl mx-auto mb-8"
        >
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700">
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for a book of the Bible..."
                className="w-full pl-10 pr-4 py-3 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-bible-gold focus:outline-none focus:ring-2 focus:ring-bible-gold/50"
              />
            </div>

            {/* Filter */}
            <div className="flex items-center gap-2">
              <Filter className="text-gray-400" size={20} />
              <div className="flex gap-2 flex-1">
                <button
                  onClick={() => setFilter('all')}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                    filter === 'all'
                      ? 'bg-bible-gold text-slate-900'
                      : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                  }`}
                >
                  All Books (66)
                </button>
                <button
                  onClick={() => setFilter('old')}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                    filter === 'old'
                      ? 'bg-bible-gold text-slate-900'
                      : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                  }`}
                >
                  Old Testament (39)
                </button>
                <button
                  onClick={() => setFilter('new')}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                    filter === 'new'
                      ? 'bg-bible-gold text-slate-900'
                      : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                  }`}
                >
                  New Testament (27)
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Books Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {books.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="mx-auto text-gray-500 mb-4" size={48} />
              <p className="text-xl text-gray-400">No books found matching your search.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {books.map((book, index) => (
                <motion.div
                  key={book.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <BookCard
                    book={book}
                    onSelectBook={handleSelectBook}
                    onGenerateSlides={handleGenerateSlides}
                  />
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Featured Slideshow */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-12 max-w-4xl mx-auto"
        >
          <div className="bg-gradient-to-r from-bible-gold/20 to-bible-blue/20 backdrop-blur-sm rounded-2xl p-8 border border-bible-gold/30">
            <h2 className="text-2xl font-bold text-bible-gold mb-4">Featured Slideshow</h2>
            <h3 className="text-xl font-semibold text-white mb-2">Daniel Chapter 7</h3>
            <p className="text-gray-300 mb-4">
              Explore Daniel's vision of the four beasts and the Ancient of Days. 
              An interactive study with photorealistic imagery and detailed biblical commentary.
            </p>
            <a
              href="/bible-studies/daniel/chapter7/"
              className="inline-block px-6 py-3 bg-bible-gold text-slate-900 rounded-lg font-semibold hover:bg-yellow-500 transition-all"
            >
              Start Study
            </a>
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Scroll to Top Button */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 z-50 bg-bible-gold text-slate-900 rounded-full p-4 shadow-2xl hover:bg-yellow-500 transition-colors hover:scale-110 active:scale-95"
            aria-label="Scroll to top"
          >
            <ArrowUp size={24} />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  )
}

export default HomePage

